import { useState } from 'react';
import { ShieldCheck } from 'lucide-react';
import { Card, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { ApiError } from '@/lib/api/client';
import { toast } from '@/hooks/useToast';
import { useUpdateDispensingSettings } from '@/features/pharmacy/useNearExpiry';
import type { DispensingSettingsAdmin } from '@/features/pharmacy/types';

/**
 * Stock adjustment approval (Phase 1 G15). A manual adjustment at or above
 * either limit becomes a request someone else approves; blank = no limit.
 * Off on install. Lives in Admin → Pharmacy Policies → Dispensing & Expiry.
 */
export function AdjustmentApprovalCard({ settings }: { settings: DispensingSettingsAdmin }) {
  const update = useUpdateDispensingSettings();
  const [units, setUnits] = useState(settings.adjustment_approval_units != null ? String(settings.adjustment_approval_units) : '');
  const [value, setValue] = useState(settings.adjustment_approval_value != null ? String(Number(settings.adjustment_approval_value)) : '');
  const [error, setError] = useState<string | null>(null);

  const dirty = units !== (settings.adjustment_approval_units != null ? String(settings.adjustment_approval_units) : '')
    || value !== (settings.adjustment_approval_value != null ? String(Number(settings.adjustment_approval_value)) : '');

  const save = () => {
    const u = units.trim() === '' ? null : Number(units);
    const v = value.trim() === '' ? null : Number(value);
    if (u != null && !(Number.isInteger(u) && u > 0)) { setError('Units must be a whole number above 0, or blank'); return; }
    if (v != null && !(v > 0)) { setError('Value must be above 0, or blank'); return; }
    setError(null);
    update.mutate({ adjustment_approval_units: u, adjustment_approval_value: v }, {
      onSuccess: () => toast({ title: 'Approval limits saved', variant: 'success' }),
      onError: (e) => setError(e instanceof ApiError ? e.message : 'Not saved'),
    });
  };

  const off = !units.trim() && !value.trim();
  return (
    <Card>
      <CardHeader><CardTitle><ShieldCheck size={15} /> Stock adjustment approval</CardTitle></CardHeader>
      <p className="mb-3 text-[11.5px] leading-snug text-ink-muted">
        A write-off, correction or return entered by hand that reaches either limit doesn’t change stock straight
        away: it waits until someone else with “Pharmacy — approve stock adjustments” approves it. Leave both blank to
        apply every adjustment at once. {off && <b>Currently off.</b>}
      </p>
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-[11.5px] text-ink-muted">
          At or above (units)
          <Input type="number" min={1} className="h-8 w-28 tabular-nums" value={units} placeholder="No limit"
            onChange={(e) => setUnits(e.target.value)} />
        </label>
        <label className="flex flex-col gap-1 text-[11.5px] text-ink-muted">
          or worth at cost (₹)
          <Input type="number" min={1} className="h-8 w-32 tabular-nums" value={value} placeholder="No limit"
            onChange={(e) => setValue(e.target.value)} />
        </label>
        <Button size="sm" disabled={!dirty} loading={update.isPending} onClick={save}>Save limits</Button>
      </div>
      {error && <div className="mt-2 text-xs text-danger-ink">{error}</div>}
    </Card>
  );
}
